/**
 * ProfileDropdown — Apple-style profile menu, opens from the top-right avatar.
 *
 * Items:
 *   • Header: avatar + name + verified email + credits
 *   • My Profile      → /app?me=1 (opens Me sheet)
 *   • Life Journey    → /app?journey=1
 *   • My Life Reports → /app?reports=1
 *   • Credit Wallet   → opens TopupSheet via window event
 *   • Settings        → /app?settings=1
 *   • Switch Account  → sign out + landing (returns user to sign-in modal)
 *   • Create New Account → sign out + landing with signup mode
 *   • Log Out         → POST /auth/logout
 *
 * All data-testids follow kebab-case for E2E; see the pytest suite in
 * /app/backend/tests/test_profile_dropdown.py.
 */
import React, { useEffect, useRef, useState } from "react";
import { User, Compass, FileText, Download, Wallet, Gift, Ticket, Settings, UserPlus, Users, LogOut } from "lucide-react";

const INDIGO = "#5D00FF";
const INK    = "#1C1C1C";
const MUTE   = "#676773";

export default function ProfileDropdown({
  user, credits, onNavigate, onLogout, onSwitchAccount, onCreateNew,
  onOpenWallet, onOpenMe, onOpenReports, onOpenJourney, onOpenSettings,
  onDownloadLatestReport, hasLatestReport,
  onOpenReferAndEarn, onOpenRedeemCode,
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const panelRef  = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (anchorRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc, true);
    document.addEventListener("touchstart", onDoc, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc, true);
      document.removeEventListener("touchstart", onDoc, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;

  const firstName = (user.name || user.email || "You").split(" ")[0];
  const picStyle  = user.picture
    ? { background: `url(${user.picture}) center/cover` }
    : { background: "linear-gradient(135deg,#5D00FF,#FF2E9A)" };

  const items = [
    { id: "profile",       label: "My Profile",         icon: User,     action: onOpenMe },
    { id: "inner-mirror",  label: "Inner Mirror 🪞",     icon: Compass,  action: () => { window.location.href = "/inner-mirror"; } },
    { id: "journey",       label: "Life Journey",       icon: Compass,  action: onOpenJourney },
    { id: "reports",       label: "My Life Reports",    icon: FileText, action: onOpenReports },
    ...(hasLatestReport
      ? [{ id: "download-report", label: "Download Latest Report", icon: Download, action: onDownloadLatestReport }]
      : []),
    { id: "wallet",        label: "Credit Wallet",      icon: Wallet,   action: onOpenWallet },
    { id: "refer-earn",    label: "Refer & Earn",       icon: Gift,     action: onOpenReferAndEarn },
    { id: "redeem-code",   label: "Redeem Code",        icon: Ticket,   action: onOpenRedeemCode },
    { id: "settings",      label: "Settings",           icon: Settings, action: onOpenSettings },
    { id: "switch",        label: "Switch Account",     icon: Users,    action: onSwitchAccount },
    { id: "newacct",       label: "Create New Account", icon: UserPlus, action: onCreateNew },
  ];

  const close = () => setOpen(false);

  return (
    <div className="relative" style={{ display: "inline-block" }} data-testid="profile-dropdown-root">
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 text-[12px] text-slate-700 hover:text-[#1C1C1C] border border-black/10 rounded-full px-3 py-1.5 bg-white/70"
        data-testid="profile-dropdown-trigger"
        style={{ cursor: "pointer" }}
      >
        <span className="w-7 h-7 rounded-full overflow-hidden" style={picStyle} aria-hidden />
        <span className="hidden sm:inline">{firstName}</span>
      </button>
      {open && (
        <div
          ref={panelRef}
          role="menu"
          data-testid="profile-dropdown-panel"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 8px)",
            minWidth: 260,
            maxWidth: 300,
            background: "rgba(255,255,255,0.98)",
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 18,
            boxShadow: "0 24px 48px rgba(20,10,60,0.14)",
            backdropFilter: "blur(18px)",
            zIndex: 60,
            overflow: "hidden",
            animation: "k-pd-in 180ms ease",
          }}
        >
          {/* Header */}
          <div style={{ padding: "16px 18px 12px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span className="w-11 h-11 rounded-full overflow-hidden" style={picStyle} aria-hidden />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, color: INK, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} data-testid="profile-dropdown-name">
                  {user.name || firstName}
                </div>
                <div style={{ fontSize: 11.5, color: MUTE, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} data-testid="profile-dropdown-email">
                  {user.email}
                </div>
              </div>
            </div>
            <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, letterSpacing: ".2em", textTransform: "uppercase", color: MUTE }}>
                KALKI Credits
              </span>
              <span style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 18, color: INDIGO, fontWeight: 500 }} data-testid="profile-dropdown-credits">
                ✦ {credits ?? "—"}
              </span>
            </div>
          </div>

          {/* Items */}
          <div style={{ padding: "6px" }}>
            {items.map(({ id, label, icon: Icon, action }) => (
              <button
                key={id}
                data-testid={`profile-dropdown-${id}`}
                onClick={() => { close(); action?.(); }}
                className="w-full text-left"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: "transparent",
                  color: INK,
                  fontSize: 13.5,
                  cursor: "pointer",
                  transition: "background 160ms ease, color 160ms ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(93,0,255,0.06)"; e.currentTarget.style.color = INDIGO; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = INK; }}
              >
                <Icon size={16} style={{ color: INDIGO, opacity: 0.9 }} />
                {label}
              </button>
            ))}
          </div>

          {/* Log out, visually separated */}
          <div style={{ borderTop: "1px solid rgba(0,0,0,0.06)", padding: 6 }}>
            <button
              data-testid="profile-dropdown-logout"
              onClick={() => { close(); onLogout?.(); }}
              className="w-full text-left"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                borderRadius: 10,
                background: "transparent",
                color: "#B91C1C",
                fontSize: 13.5,
                cursor: "pointer",
                transition: "background 160ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(185,28,28,0.07)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <LogOut size={16} />
              Log out
            </button>
          </div>
          <style>{`
            @keyframes k-pd-in {
              from { opacity: 0; transform: translateY(-6px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
